// src/pages/Dispatchers.jsx
import React, { useState, useEffect } from "react";
import axios from "axios";
import { API_URL } from "../config";

const Dispatchers = () => {
  const [tab, setTab] = useState("dispatchers"); // "dispatchers" | "drivers"

  // Dispatchers state
  const [dispatchers, setDispatchers] = useState([]);
  const [savedDispatchers, setSavedDispatchers] = useState([]);
  const [savingDispatcher, setSavingDispatcher] = useState(null);
  const [deletingDispatcher, setDeletingDispatcher] = useState(null);
  const [searchTermDispatchers, setSearchTermDispatchers] = useState("");
  const [searchResultDispatcher, setSearchResultDispatcher] = useState(null);

  // Drivers state
  const [drivers, setDrivers] = useState([]);
  const [savedDrivers, setSavedDrivers] = useState([]);
  const [savingDriver, setSavingDriver] = useState(null);
  const [deletingDriver, setDeletingDriver] = useState(null);
  const [searchTermDrivers, setSearchTermDrivers] = useState("");
  const [searchResultDriver, setSearchResultDriver] = useState(null);

  // Shared UI state
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const token = localStorage.getItem("access_token");
  const authHeader = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    (async () => {
      try {
        setLoading(true);
        setError(null);

        // Parallel fetches
        const [
          dispatchersRes,
          savedDispatchersRes,
          driversRes,
          savedDriversRes,
        ] = await Promise.all([
          axios.get(`${API_URL}/user/dispatchers`, { headers: authHeader }),
          axios.get(`${API_URL}/user/dispatchers/saved`, {
            headers: authHeader,
          }),
          axios.get(`${API_URL}/user/drivers`, { headers: authHeader }),
          axios.get(`${API_URL}/user/drivers/saved`, { headers: authHeader }),
        ]);

        setDispatchers(dispatchersRes.data || []);
        setSavedDispatchers(savedDispatchersRes.data || []);
        setDrivers(driversRes.data || []);
        setSavedDrivers(savedDriversRes.data || []);
      } catch (err) {
        console.error("Error loading lists:", err);
        setError("Failed to load data. Please try again later.");
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // load once

  // ----- Helpers to compute "available to add" lists -----
  const savedDispatcherEmails = new Set(
    savedDispatchers.map((d) => d.dispatcher_email),
  );
  const availableDispatchers = dispatchers.filter(
    (d) => !savedDispatcherEmails.has(d.email),
  );

  const savedDriverEmails = new Set(savedDrivers.map((d) => d.driver_email));
  const availableDrivers = drivers.filter(
    (d) => !savedDriverEmails.has(d.email),
  );

  // ----- Save / Delete (Dispatchers) -----
  const handleSaveDispatcher = async (dispatcher, e) => {
    e?.stopPropagation?.();
    try {
      setSavingDispatcher(dispatcher.email);
      await axios.post(
        `${API_URL}/user/dispatchers/save?dispatcher_email=${encodeURIComponent(
          dispatcher.email,
        )}`,
        {},
        { headers: authHeader },
      );
      // Refresh saved list
      const res = await axios.get(`${API_URL}/user/dispatchers/saved`, {
        headers: authHeader,
      });
      setSavedDispatchers(res.data || []);
      alert("Dispatcher saved successfully!");
    } catch (err) {
      console.error("Error saving dispatcher:", err);
      alert("Failed to save dispatcher. Please try again.");
    } finally {
      setSavingDispatcher(null);
    }
  };

  const handleDeleteDispatcher = async (dispatcher, e) => {
    e?.stopPropagation?.();
    if (!window.confirm("Remove this dispatcher?")) return;
    try {
      setDeletingDispatcher(dispatcher.dispatcher_email);
      await axios.delete(
        `${API_URL}/user/dispatchers/saved/${encodeURIComponent(
          dispatcher.dispatcher_email,
        )}`,
        { headers: authHeader },
      );
      setSavedDispatchers((prev) =>
        prev.filter((d) => d.dispatcher_email !== dispatcher.dispatcher_email),
      );
    } catch (err) {
      console.error("Error deleting dispatcher:", err);
      alert("Failed to delete dispatcher. Please try again.");
    } finally {
      setDeletingDispatcher(null);
    }
  };

  // ----- Save / Delete (Drivers) -----
  const handleSaveDriver = async (driver, e) => {
    e?.stopPropagation?.();
    try {
      setSavingDriver(driver.email);
      await axios.post(
        `${API_URL}/user/drivers/save?driver_email=${encodeURIComponent(
          driver.email,
        )}`,
        {},
        { headers: authHeader },
      );
      const res = await axios.get(`${API_URL}/user/drivers/saved`, {
        headers: authHeader,
      });
      setSavedDrivers(res.data || []);
      alert("Driver saved successfully!");
    } catch (err) {
      console.error("Error saving driver:", err);
      alert("Failed to save driver. Please try again.");
    } finally {
      setSavingDriver(null);
    }
  };

  const handleDeleteDriver = async (driver, e) => {
    e?.stopPropagation?.();
    if (!window.confirm("Remove this driver?")) return;
    try {
      setDeletingDriver(driver.driver_email);
      await axios.delete(
        `${API_URL}/user/drivers/saved/${encodeURIComponent(driver.driver_email)}`,
        { headers: authHeader },
      );
      setSavedDrivers((prev) =>
        prev.filter((d) => d.driver_email !== driver.driver_email),
      );
    } catch (err) {
      console.error("Error deleting driver:", err);
      alert("Failed to delete driver. Please try again.");
    } finally {
      setDeletingDriver(null);
    }
  };

  // ----- Search handlers -----
  const handleSearchDispatchers = (e) => {
    e.preventDefault();
    const term = searchTermDispatchers.trim().toLowerCase();
    if (!term) return setSearchResultDispatcher(null);
    const exact = availableDispatchers.find(
      (d) => d.email.toLowerCase() === term,
    );
    setSearchResultDispatcher(exact || null);
  };

  const handleSearchDrivers = (e) => {
    e.preventDefault();
    const term = searchTermDrivers.trim().toLowerCase();
    if (!term) return setSearchResultDriver(null);
    const exact = availableDrivers.find((d) => d.email.toLowerCase() === term);
    setSearchResultDriver(exact || null);
  };

  if (loading) {
    return (
      <div
        style={{
          display: "flex",
          justifyContent: "center",
          padding: 40,
          color: "var(--text)",
        }}
      >
        <div style={{ textAlign: "center" }}>
          <div
            style={{
              width: 40,
              height: 40,
              border: "3px solid var(--primary)",
              borderTop: "3px solid transparent",
              borderRadius: "50%",
              animation: "spin 1s linear infinite",
              margin: "0 auto 12px",
            }}
          />
          Loading…
        </div>
        <style>{`@keyframes spin{0%{transform:rotate(0)}100%{transform:rotate(360deg)}}`}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div
        style={{
          padding: 20,
          color: "#ff4444",
          textAlign: "center",
          backgroundColor: "rgba(255,68,68,0.1)",
          borderRadius: 8,
          margin: 20,
        }}
      >
        {error}
      </div>
    );
  }

  // ----- Render cards helpers -----
  const DispatcherCard = ({ item }) => (
    <div
      style={{
        backgroundColor: "var(--surface)",
        borderRadius: 10,
        padding: 20,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        position: "relative",
      }}
    >
      <h3
        style={{
          fontSize: "1.1rem",
          marginBottom: 10,
          color: "var(--primary)",
        }}
      >
        {item.dispatcher_name || item.name}
      </h3>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontSize: ".9rem",
          color: "#8b949e",
        }}
      >
        {(item.dispatcher_company || item.company_name) && (
          <div>
            <strong>Company:</strong>{" "}
            {item.dispatcher_company || item.company_name}
          </div>
        )}
        <div>
          <strong>Email:</strong> {item.dispatcher_email || item.email}
        </div>
        {(item.dispatcher_phone || item.phone_number) && (
          <div>
            <strong>Phone:</strong> {item.dispatcher_phone || item.phone_number}
          </div>
        )}
      </div>

      {item.dispatcher_email ? (
        <button
          onClick={(e) => handleDeleteDispatcher(item, e)}
          disabled={deletingDispatcher === item.dispatcher_email}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            backgroundColor: "var(--primary)",
            color: "var(--button-text)",
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          {deletingDispatcher === item.dispatcher_email
            ? "Removing…"
            : "Remove"}
        </button>
      ) : (
        <button
          onClick={(e) => handleSaveDispatcher(item, e)}
          disabled={savingDispatcher === item.email}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            backgroundColor: "var(--primary)",
            color: "var(--button-text)",
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          {savingDispatcher === item.email ? "Adding…" : "Add"}
        </button>
      )}
    </div>
  );

  const DriverCard = ({ item }) => (
    <div
      style={{
        backgroundColor: "var(--surface)",
        borderRadius: 10,
        padding: 20,
        boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
        position: "relative",
      }}
    >
      <h3
        style={{
          fontSize: "1.1rem",
          marginBottom: 10,
          color: "var(--primary)",
        }}
      >
        {item.driver_name || item.name}
      </h3>
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          gap: 8,
          fontSize: ".9rem",
          color: "#8b949e",
        }}
      >
        {(item.driver_company || item.company_name) && (
          <div>
            <strong>Company:</strong> {item.driver_company || item.company_name}
          </div>
        )}
        <div>
          <strong>Email:</strong> {item.driver_email || item.email}
        </div>
        {(item.driver_phone || item.phone_number) && (
          <div>
            <strong>Phone:</strong> {item.driver_phone || item.phone_number}
          </div>
        )}
      </div>

      {item.driver_email ? (
        <button
          onClick={(e) => handleDeleteDriver(item, e)}
          disabled={deletingDriver === item.driver_email}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            backgroundColor: "var(--primary)",
            color: "var(--button-text)",
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          {deletingDriver === item.driver_email ? "Removing…" : "Remove"}
        </button>
      ) : (
        <button
          onClick={(e) => handleSaveDriver(item, e)}
          disabled={savingDriver === item.email}
          style={{
            position: "absolute",
            top: 10,
            right: 10,
            backgroundColor: "var(--primary)",
            color: "var(--button-text)",
            border: "none",
            borderRadius: 6,
            padding: "8px 12px",
            cursor: "pointer",
          }}
        >
          {savingDriver === item.email ? "Adding…" : "Add"}
        </button>
      )}
    </div>
  );

  return (
    <div
      style={{
        padding: "40px 20px",
        maxWidth: 1200,
        margin: "0 auto",
        color: "var(--text)",
      }}
    >
      {/* Tabs */}
      <div
        style={{
          display: "flex",
          gap: 8,
          justifyContent: "center",
          marginBottom: 24,
        }}
      >
        <button
          onClick={() => setTab("dispatchers")}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border:
              tab === "dispatchers"
                ? "2px solid var(--primary)"
                : "1px solid var(--border)",
            background:
              tab === "dispatchers" ? "var(--surface)" : "transparent",
            cursor: "pointer",
            fontWeight: 600,
            color: "var(--text)",
          }}
        >
          Dispatchers
        </button>
        <button
          onClick={() => setTab("drivers")}
          style={{
            padding: "10px 16px",
            borderRadius: 10,
            border:
              tab === "drivers"
                ? "2px solid var(--primary)"
                : "1px solid var(--border)",
            background: tab === "drivers" ? "var(--surface)" : "transparent",
            cursor: "pointer",
            fontWeight: 600,
            color: "var(--text)",
          }}
        >
          Drivers
        </button>
      </div>

      {tab === "dispatchers" ? (
        <>
          <h1
            style={{ fontSize: "2rem", marginBottom: 20, textAlign: "center" }}
          >
            My Dispatchers
          </h1>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 20,
              padding: 20,
              marginBottom: 40,
            }}
          >
            {savedDispatchers.length === 0 ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                No saved dispatchers yet.
              </div>
            ) : (
              savedDispatchers.map((d) => (
                <DispatcherCard key={d.dispatcher_email} item={d} />
              ))
            )}
          </div>

          <h1
            style={{ fontSize: "2rem", marginBottom: 20, textAlign: "center" }}
          >
            Search Dispatchers
          </h1>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <form
              onSubmit={handleSearchDispatchers}
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 30,
                width: "70%",
                padding: "0 20px",
              }}
            >
              <input
                type="text"
                placeholder="Enter dispatcher email..."
                value={searchTermDispatchers}
                onChange={(e) => setSearchTermDispatchers(e.target.value)}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  padding: "12px 20px",
                  fontSize: "1rem",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  backgroundColor: "var(--background)",
                  color: "var(--text)",
                }}
              />
              <button
                type="submit"
                style={{
                  padding: "12px 24px",
                  fontSize: "1rem",
                  background: "var(--primary)",
                  color: "var(--button-text)",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Search
              </button>
            </form>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 20,
              padding: 20,
            }}
          >
            {searchResultDispatcher ? (
              <DispatcherCard item={searchResultDispatcher} />
            ) : searchTermDispatchers ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                No dispatcher found with that email.
              </div>
            ) : (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                Enter an email address to search for a dispatcher.
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <h1
            style={{ fontSize: "2rem", marginBottom: 20, textAlign: "center" }}
          >
            My Drivers
          </h1>
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 20,
              padding: 20,
              marginBottom: 40,
            }}
          >
            {savedDrivers.length === 0 ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                No saved drivers yet.
              </div>
            ) : (
              savedDrivers.map((d) => (
                <DriverCard key={d.driver_email} item={d} />
              ))
            )}
          </div>

          <h1
            style={{ fontSize: "2rem", marginBottom: 20, textAlign: "center" }}
          >
            Search Drivers
          </h1>
          <div style={{ display: "flex", justifyContent: "center" }}>
            <form
              onSubmit={handleSearchDrivers}
              style={{
                display: "flex",
                gap: 10,
                marginBottom: 30,
                width: "70%",
                padding: "0 20px",
              }}
            >
              <input
                type="text"
                placeholder="Enter driver email..."
                value={searchTermDrivers}
                onChange={(e) => setSearchTermDrivers(e.target.value)}
                style={{
                  width: "100%",
                  maxWidth: 400,
                  padding: "12px 20px",
                  fontSize: "1rem",
                  border: "1px solid var(--border)",
                  borderRadius: 8,
                  backgroundColor: "var(--background)",
                  color: "var(--text)",
                }}
              />
              <button
                type="submit"
                style={{
                  padding: "12px 24px",
                  fontSize: "1rem",
                  background: "var(--primary)",
                  color: "var(--button-text)",
                  border: "none",
                  borderRadius: 8,
                  cursor: "pointer",
                }}
              >
                Search
              </button>
            </form>
          </div>

          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
              gap: 20,
              padding: 20,
            }}
          >
            {searchResultDriver ? (
              <DriverCard item={searchResultDriver} />
            ) : searchTermDrivers ? (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                No driver found with that email.
              </div>
            ) : (
              <div
                style={{
                  gridColumn: "1 / -1",
                  textAlign: "center",
                  padding: 40,
                  color: "#8b949e",
                  background: "var(--surface)",
                  borderRadius: 10,
                }}
              >
                Enter an email address to search for a driver.
              </div>
            )}
          </div>
        </>
      )}

      <style>{`
        @keyframes spin { 0% { transform: rotate(0deg) } 100% { transform: rotate(360deg) } }
      `}</style>
    </div>
  );
};

export default Dispatchers;
